<template>
    <FormSection title="Physicians" type="his-section" :full-height="false">
        <template v-slot:content>
            <section class="row pt-3 pb-0 g-0">
                <div class="col-12 col-lg-12 px-4 col-md-12 g-0">
                    <h6 class="mb-2 d-flex align-items-center"><b>Attending Physician</b></h6>
                    <p>
                        Use the filters below to narrow down physicians by city and/or state. To search for a physician,
                        enter the physician's name or NPI in the respective search field.
                    </p>
                    <section class="form-group col-12 d-flex flex-column mb-0">
                        <b class="me-2">Filters</b>
                        <section class="col-12 flex-row py-2 px-3 row g-0">
                            <div class="form-group col-12 col-lg-4 pe-lg-3">
                                <label for="referral-source"> City </label>
                                <TypeaheadInput
                                    :getData="getReferringPhysicianListItem"
                                    v-on:valueChanged="referringPhysicianFiltersCity"
                                    :results-limit="1"
                                    itemName="city"
                                    placeholder="Select City"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-4 pe-lg-3">
                                <label for="referral-source"> State </label>

                                <TypeaheadInput
                                    :getData="getReferringPhysicianListItem"
                                    v-on:valueChanged="referringPhysicianFiltersState"
                                    :results-limit="2"
                                    itemName="state"
                                    placeholder="Select State"
                                />
                            </div>
                        </section>
                    </section>
                    <section class="form-group col-12 d-flex flex-column">
                        <b class="me-2">Search</b>
                        <section class="col-12 flex-row px-3 py-2 row g-0 align-items-center">
                            <div class="form-group col-12 col-lg-4">
                                <label for="patient-first-name">
                                    Attending Physician
                                    <span class="text-danger">*</span>
                                    <a
                                        class="px-1"
                                        tabindex="0"
                                        role="button"
                                        v-custom-popover="{ content: sanitizeMarkup(attendingPhysicianTooltip) }"
                                    >
                                        <i class="fas fa-question-circle text-dark font-size-xs"></i>
                                    </a>
                                </label>
                                <TypeaheadInput
                                    :getData="getPhysicianList"
                                    v-on:valueChanged="attendingPhysicianChanged"
                                    placeholder="Start Typing..."
                                    :value="attendingPhysician"
                                    :isError="attendingPhysicianError"
                                />
                            </div>
                            <div class="mt-3 col-12 col-lg-1 text-center">
                                <strong>OR</strong>
                            </div>
                            <div class="form-group col-12 col-lg-4 pe-lg-3">
                                <label for="referral-source"> NPI </label>
                                <TypeaheadInput
                                    :getData="getReferringPhysicianListItem"
                                    v-on:valueChanged="referringPhysicianFiltersNpiChange"
                                    :results-limit="3"
                                    itemName="npi"
                                    :key-updated="npiChanged('referring')"
                                    placeholder="Search By NPI"
                                />
                            </div>
                        </section>
                    </section>
                    <div v-show="physicianInfo.FirstName" class="physician-information mb-3">
                        <p>Physician Name: {{ physicianInfo.DisplayName }},</p>
                        <p>NPI: {{ physicianInfo.NPI }}</p>
                        <p>Address: {{ physicianInfo.AddressLine1 }},</p>
                        <p v-show="physicianInfo.AddressLine2">{{ physicianInfo.AddressLine2 }},</p>
                        <p>
                            <span class="me-1">{{ physicianInfo.AddressCity }},</span>
                            <span class="me-1">{{ physicianInfo.AddressStateCode }},</span>
                            <span class="me-1">{{ physicianInfo.AddressZipCode }},</span>
                        </p>
                        <p>
                            <span v-show="physicianInfo.PhoneWork" class="me-1"
                                ><i class="fas fa-phone-alt me-1"></i>Phone:{{ physicianInfo.PhoneWork }}</span
                            >
                            <span v-show="physicianInfo.FaxNumber">
                                <i class="fas fa-fax me-1"></i>, Fax: {{ physicianInfo.FaxNumber }}</span
                            >
                        </p>
                        <p v-show="physicianInfo.EmailAddress">
                            <i class="fas fa-envelope me-1 me-1"></i>Email Address: {{ physicianInfo.EmailAddress }}
                        </p>
                    </div>
                    <div class="form-check custom-checkbox mb-3">
                        <input
                            type="checkbox"
                            id="physicianContacted"
                            class="form-check-input au-target"
                            v-model="referral.Physicians.IsPhysicianContacted"
                        />
                        <label class="form-check-label" for="physicianContacted">
                            <span class="me-1"
                                >The attending physician agreed to provide ongoing care for the patient.</span
                            >
                        </label>
                        <a
                            tabindex="0"
                            role="button"
                            v-custom-popover="{
                                content: sanitizeMarkup(attendingPhysicianContactedTooltip)
                            }"
                        >
                            <i class="fas fa-question-circle text-dark font-size-xs"></i>
                        </a>
                    </div>

                    <section class="form-group">
                        <button class="btn btn-xs btn-primary mb-2" @click="openNewPhysicianDialog()">
                            New Physician
                        </button>
                    </section>

                    <section class="form-group">
                        <h6 class="mb-3 d-flex align-items-center">
                            <b>Other Physicians on the Case</b>
                        </h6>

                        <div v-for="(physician, index) in referral.Physicians.AgencyPhysicianDetails">
                            <FormSection :application-id="application" type="his-section">
                                <template #content>
                                    <div class="col-12 col-lg-12 px-4 col-md-12 g-0">
                                        <div class="my-2 d-flex align-items-center font-size-md">
                                            <b class="me-2">Additional Physician {{ index + 1 }}</b>
                                            <button
                                                class="btn table-btn btn-outline-danger me-1 ms-auto"
                                                @click="removeAdditionalPhysician(index)"
                                            >
                                                <i class="fas fa-trash-alt"></i>
                                            </button>
                                        </div>
                                        <p>
                                            Use the filters below to narrow down physicians by city and/or state. To
                                            search for a physician, enter the physician's name or NPI in the respective
                                            search field.
                                        </p>
                                        <section class="form-group col-12 d-flex flex-column mb-0">
                                            <b class="me-2">Filters</b>
                                            <section class="col-12 flex-row py-2 px-3 row g-0">
                                                <div class="form-group col-12 col-lg-4 pe-lg-3">
                                                    <label for="referral-source"> City </label>
                                                    <TypeaheadInput
                                                        :getData="getReferringPhysicianListItem"
                                                        v-on:valueChanged="referringPhysicianFiltersCity"
                                                        :results-limit="1"
                                                        itemName="city"
                                                        placeholder="Select City"
                                                    />
                                                </div>
                                                <div class="form-group col-12 col-lg-4 pe-lg-3">
                                                    <label for="referral-source"> State </label>

                                                    <TypeaheadInput
                                                        :getData="getReferringPhysicianListItem"
                                                        v-on:valueChanged="referringPhysicianFiltersState"
                                                        :results-limit="2"
                                                        itemName="state"
                                                        placeholder="Select State"
                                                    />
                                                </div>
                                            </section>
                                        </section>
                                        <section class="form-group col-12 d-flex flex-column">
                                            <b class="me-2">Search</b>
                                            <section class="col-12 flex-row px-3 py-2 row g-0 align-items-center">
                                                <div class="form-group col-12 col-lg-4">
                                                    <label for="patient-first-name">
                                                        Attending Physician
                                                        <a
                                                            tabindex="0"
                                                            role="button"
                                                            v-custom-popover="{
                                                                content: sanitizeMarkup(attendingPhysicianTooltip)
                                                            }"
                                                        >
                                                            <i
                                                                class="fas fa-question-circle text-dark font-size-xs"
                                                            ></i>
                                                        </a>
                                                    </label>
                                                    <TypeaheadInput
                                                        :getData="getPhysicianList"
                                                        v-on:valueChanged="otherPhysicianChanged($event, index)"
                                                        placeholder="Start Typing..."
                                                        :init-value="otherPhysicians[index]"
                                                    />
                                                </div>
                                                <div class="mt-3 col-12 col-lg-1 text-center">
                                                    <strong>OR</strong>
                                                </div>
                                                <div class="form-group col-12 col-lg-4 pe-lg-3">
                                                    <label for="referral-source"> NPI </label>
                                                    <TypeaheadInput
                                                        :getData="getReferringPhysicianListItem"
                                                        v-on:valueChanged="
                                                            referringPhysicianFiltersNpiChange($event, index)
                                                        "
                                                        :results-limit="3"
                                                        itemName="npi"
                                                        placeholder="Search By NPI"
                                                    />
                                                </div>
                                            </section>
                                        </section>
                                        <div
                                            v-show="additionalPhysicians[index]?.FirstName"
                                            class="physician-information mb-3"
                                        >
                                            <p>Physician Name: {{ additionalPhysicians[index]?.DisplayName }},</p>
                                            <p>NPI: {{ additionalPhysicians[index]?.NPI }}</p>
                                            <p>Address: {{ additionalPhysicians[index]?.AddressLine1 }},</p>
                                            <p v-show="additionalPhysicians[index]?.AddressLine2">
                                                {{ additionalPhysicians[index]?.AddressLine2 }},
                                            </p>
                                            <p>
                                                <span class="me-1"
                                                    >{{ additionalPhysicians[index]?.AddressCity }},</span
                                                >
                                                <span class="me-1"
                                                    >{{ additionalPhysicians[index]?.AddressStateCode }},</span
                                                >
                                                <span class="me-1"
                                                    >{{ additionalPhysicians[index]?.AddressZipCode }},</span
                                                >
                                            </p>
                                            <p>
                                                <span v-show="additionalPhysicians[index]?.PhoneWork" class="me-1"
                                                    ><i class="fas fa-phone-alt me-1"></i>Phone:
                                                    {{ additionalPhysicians[index]?.PhoneWork }}</span
                                                >
                                                <span v-show="additionalPhysicians[index]?.FaxNumber">
                                                    <i class="fas fa-fax me-1"></i>, Fax:
                                                    {{ additionalPhysicians[index]?.FaxNumber }}</span
                                                >
                                            </p>
                                            <p v-show="additionalPhysicians[index]?.EmailAddress">
                                                <i class="fas fa-envelope me-1 me-1"></i>Email Address:
                                                {{ additionalPhysicians[index]?.EmailAddress }}
                                            </p>
                                        </div>
                                    </div>
                                </template>
                            </FormSection>
                        </div>

                        <button
                            class="btn btn-xs btn-primary mb-2"
                            @click="addAdditionalPhysician()"
                            :disabled="additionalPhysicianIndex >= 10"
                        >
                            Add
                        </button>
                    </section>
                </div>
            </section>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./ReferralPhysicians.ts"></script>

<style lang="scss">
.physician-information {
    p {
        margin: 0;
    }
}
</style>
